import { IHeatmap } from './HeatmapHelper';

function rearangementRows(heatmap: IHeatmap, indexes: number[]): IHeatmap {
  const mat: number[][] = [];
  const rows: Array<{ name: string; description: string; id: string }> = [];
  indexes?.forEach(x => {
    mat.push(heatmap.matrix[x]);
    rows.push(heatmap.axisYLabels[x]);
  });
  heatmap.matrix = mat;
  heatmap.axisYLabels = rows;

  return heatmap;
}

function rearangementColumns(heatmap: IHeatmap, indexes: number[]): IHeatmap {
  const mat = heatmap.matrix && heatmap.matrix.map(x => x.map(() => 0));
  const columns: Array<{ name: string; description: string }> = [];
  indexes?.forEach((x, j) => {
    for (let i = 0; i < mat.length; ++i) {
      mat[i][j] = heatmap.matrix[i][x];
    }
    columns.push(heatmap.axisXLabels[x]);
  });
  heatmap.matrix = mat;
  heatmap.axisXLabels = columns;

  return heatmap;
}

export function sortRows(heatmap: IHeatmap, sortByAlpha: boolean, sortByRank: boolean): IHeatmap {
  let indexes: number[] = [];
  if (sortByAlpha) {
    indexes =
      heatmap.axisYLabels &&
      heatmap.axisYLabels
        .map((x, i) => {
          return { value: x, index: i };
        })
        .sort((x1, x2) => {
          if (x1.value.name > x2.value.name) {
            return 1;
          }

          if (x1.value.name < x2.value.name) {
            return -1;
          }

          return 0;
        })
        .map(x => x.index);
  } else if (sortByRank) {
    const orderIndex: any = heatmap.axisXLabels.findIndex((l: any) => l.name === 'order');

    indexes =
      heatmap.matrix &&
      heatmap.matrix
        .map((x, i) => {
          return {
            value: x,
            index: i,
          };
        })
        .sort((x1: any, x2: any) => x2.value[orderIndex] - x1.value[orderIndex])
        .map(x => x.index);
  } else {
    indexes =
      heatmap.matrix &&
      heatmap.matrix
        .map((x, i) => {
          return {
            value: x.reduce((sum, current) => sum + current, 0),
            index: i,
          };
        })
        .sort((x1, x2) => x2.value - x1.value)
        .map(x => x.index);
  }

  if (indexes?.length === 0) {
    return heatmap;
  }

  return rearangementRows(heatmap, indexes);
}

export function sortRowsByColumn(heatmap: IHeatmap, column: string, isAscending: boolean): IHeatmap {
  const index = heatmap.axisXLabels.findIndex(l => l.name === column);
  if (index < 0) return heatmap;
  const colValues = heatmap.matrix.map((m, i) => ({ v: m[index], i }));
  const sorted = colValues.sort((a, b) => (isAscending ? a.v - b.v : b.v - a.v));

  const axisYLabels = sorted.map(s => heatmap.axisYLabels[s.i]);
  const matrix = sorted.map(s => heatmap.matrix[s.i]);
  return { ...heatmap, axisYLabels, matrix };
}

export function sortColumnsByRow(heatmap: IHeatmap, row: string, isAscending: boolean): IHeatmap {
  const index = heatmap.axisYLabels.findIndex(l => l.name === row);
  if (index < 0) return heatmap;
  const rowValues = heatmap.matrix[index].map((v, i) => ({ v, i }));
  const sorted = rowValues.sort((a, b) => (isAscending ? a.v - b.v : b.v - a.v));

  const axisXLabels = sorted.map(s => heatmap.axisXLabels[s.i]);
  const matrix = heatmap.matrix.map(row => sorted.map(s => row[s.i]));
  return { ...heatmap, axisXLabels, matrix };
}

export function sortColumns(heatmap: IHeatmap, sortByAlpha: boolean): IHeatmap {
  let indexes: number[] = [];

  if (sortByAlpha) {
    // Sorted
    indexes =
      heatmap.axisXLabels &&
      heatmap.axisXLabels
        .map((x, i) => {
          return { value: x, index: i };
        })
        .sort((x1, x2) => {
          if (x1.value.name > x2.value.name) {
            return 1;
          }

          if (x1.value.name < x2.value.name) {
            return -1;
          }

          return 0;
        })
        .map(x => x.index);
  } else {
    // Rank by Sum
    const lenRows = heatmap?.axisYLabels?.length;
    const lenCols = heatmap?.axisXLabels?.length;
    // tslint:disable-next-line: prefer-array-literal
    let sortedArray =
      heatmap?.axisXLabels &&
      heatmap?.axisXLabels.map((x, i) => {
        return { value: 0, index: i };
      });
    for (let j = 0; j < lenCols; ++j) {
      for (let i = 0; i < lenRows; ++i) {
        sortedArray[j].value += heatmap.matrix[i][j];
      }
    }
    sortedArray = sortedArray?.sort((x1, x2) => x2.value - x1.value);
    indexes = sortedArray?.map(x => x.index);
  }

  if (indexes?.length === 0) {
    return heatmap;
  }

  return rearangementColumns(heatmap, indexes);
}
